/**
 * An enhanced details summary element that supports height transitions.
 * Summarised content must be wrapped in a single element to be supported and should
 * be given a unique ID for best accessibility.
 */
class DetailsAccordion extends HTMLElement {
  closing = false;
  opening = false;
  animation = null;

  constructor() {
    super();
    this.mainDetails = this.querySelector("details");
    this.mainSummary = this.querySelector("summary");

    this.mainSummary.addEventListener("click", this.onClick.bind(this));

    this.mainSummary.setAttribute("role", "button");
    this.mainSummary.setAttribute("aria-expanded", this.mainDetails.open);
    this.mainSummary.setAttribute(
      "aria-controls",
      this.mainSummary.nextElementSibling?.id
    );

    this.duration = Number(this.dataset.duration) || 0;
  }

  onClick(event) {
    event.preventDefault();

    this.mainDetails.style.setProperty("overflow", "hidden");

    if (!this.mainDetails.open || this.closing) {
      this.open();
    } else if (this.mainDetails.open || this.opening) {
      this.close();
    }
  }

  handleAnimationFinish(open) {
    this.mainDetails.open = open;
    this.closing = false;
    this.opening = false;
    this.animation = null;

    this.mainDetails.style.removeProperty("height");
    this.mainDetails.style.removeProperty("overflow");
    this.mainDetails.classList.remove('closing');
  }

  open() {
    this.opening = true;

    const startHeight = this.mainSummary.offsetHeight + "px";
    const endHeight =
      this.mainSummary.offsetHeight +
      this.mainSummary.nextElementSibling.offsetHeight +
      "px";

    this.mainDetails.style.setProperty("height", startHeight);

    this.mainDetails.open = true;

    this.mainSummary.setAttribute("aria-expanded", "true");

    requestAnimationFrame(() => {
      if (this.animation) {
        this.animation.cancel();
      }

      this.animation = this.mainDetails.animate(
        {
          height: [startHeight, endHeight],
        },
        {
          duration: this.duration,
          easing: "ease",
        }
      );
      this.animation.onfinish = () => this.handleAnimationFinish(true);
      this.animation.oncancel = () => (this.closing = false);
    });
  }

  close() {
    this.closing = true;

    this.mainDetails.classList.add('closing');

    const startHeight = this.mainDetails.offsetHeight + "px";
    const endHeight = this.mainSummary.offsetHeight + "px";

    this.mainSummary.setAttribute("aria-expanded", "false");
    
    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.mainDetails.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: this.duration,
        easing: "ease",
      }
    );

    this.animation.onfinish = () => this.handleAnimationFinish(false);
    this.animation.oncancel = () => {
      this.closing = false;
      this.mainDetails.classList.remove('closing');
    };
  }
}

customElements.define("details-accordion", DetailsAccordion);
